import type { CardProps } from './Card';
import type { Agenda, Module } from 'types/modules';
import { resolveAgendaName } from 'utils/resolveAgendaName';

export const MODULE_WIDGET_MAX_SUBORDINATE_ITEMS = 5;

type ModuleWidgetAgendaOptions = {
  pinnedOnly?: boolean;
  maxItems?: number;
};

export const mapAgendaToCardItem = (agenda: Agenda): Pick<CardProps, 'title' | 'agendaId' | 'sourceType' | 'isPinned'> => ({
  title: resolveAgendaName(agenda),
  agendaId: agenda.agendaId,
  sourceType: agenda.systemType,
  isPinned: agenda.isPinned,
});

export const filterVisibleAgendas = (agendas: Agenda[], { pinnedOnly = false }: ModuleWidgetAgendaOptions = {}) =>
  agendas.filter((agenda) => !agenda.hideInOverview && (!pinnedOnly || agenda.isPinned));

export const mapAgendasToCardItems = (
  agendas: Agenda[],
  { pinnedOnly = false, maxItems = MODULE_WIDGET_MAX_SUBORDINATE_ITEMS }: ModuleWidgetAgendaOptions = {}
) => filterVisibleAgendas(agendas, { pinnedOnly }).slice(0, maxItems).map(mapAgendaToCardItem);

export const getModuleCardItems = (module: Module, options?: ModuleWidgetAgendaOptions) => mapAgendasToCardItems(module.agendas, options);

export const getChildModulesWithAgendas = (module: Module, options?: ModuleWidgetAgendaOptions): Omit<Module, 'childs'>[] | undefined => {
  const { pinnedOnly = false, maxItems = MODULE_WIDGET_MAX_SUBORDINATE_ITEMS } = options ?? {};
  const children = module.childs?.filter((child) => child.agendas.some((agenda) => !agenda.hideInOverview && (!pinnedOnly || agenda.isPinned)));

  if (!children?.length) {
    return undefined;
  }

  return children.slice(0, maxItems).map((child) => ({
    ...child,
    agendas: filterVisibleAgendas(child.agendas, { pinnedOnly }).slice(0, maxItems),
  }));
};
